import type { OperationalProcess, OperationalAttention } from '../processes/operationalProcessTypes';

export type UserWorkloadView = {
  userId: number;
  activeProcesses: number;
  overdueProcesses: number;
  blockingProcesses: number;
  criticalProcesses: number;
  attentionLoad: number;
  slaViolations: number;
  queuePressure: number;
  workloadScore: number;
  avgResolutionTimeMs: number | null;
};

export type DepartmentHealthView = {
  department: string;
  totalProcesses: number;
  overdueCount: number;
  status: string;
  workloadPressure: number;
  slaRisk: boolean;
};

export type OperationalInboxSummary = {
  workload: UserWorkloadView;
  departmentHealth: DepartmentHealthView[];
  queues: {
    myProcesses: number;
    requiresAttention: number;
    overdue: number;
    blocking: number;
    finance: number;
    incidents: number;
    waitingReview: number;
  };
  priorityCounts: Record<string, number>;
  attentionGroups: Array<{ key: string; title: string; count: number; severity: string }>;
};

export type RouteWorkloadStripModel = {
  criticalCount: number;
  highAttentionCount: number;
  overdueCount: number;
  overloadedDepartments: string[];
};

export function buildRouteWorkloadStrip(
  payload: {
    processes?: OperationalProcess[];
    attentions?: OperationalAttention[];
    departmentHealth?: DepartmentHealthView[];
    metrics?: { overdueCount?: number };
  } | null | undefined
): RouteWorkloadStripModel | null {
  if (!payload) return null;
  const processes = payload.processes ?? [];
  const criticalCount = processes.filter((p) => p.priority === 'CRITICAL').length;
  const highAttentionCount = (payload.attentions ?? []).length;
  const overdueCount =
    payload.metrics?.overdueCount ??
    processes.filter((p) => p.ownership?.overdue.isOverdue).length;
  const overloadedDepartments = (payload.departmentHealth ?? [])
    .filter((d) => d.status === 'overloaded' || d.status === 'critical' || d.status === 'sla_risk')
    .map((d) => d.department);
  if (!criticalCount && !highAttentionCount && !overdueCount && !overloadedDepartments.length) {
    return null;
  }
  return { criticalCount, highAttentionCount, overdueCount, overloadedDepartments };
}
